import { createStyles } from 'antd-style';
import  colorToken  from '../../utils/colorToken';

export const useMarkdownEditorStyles = createStyles(({ token, css }) => ({
  editorContainer: css`
    border: 1px solid var(--color-border, ${colorToken.colorBorder});
    border-radius: ${token.borderRadius}px;
    overflow: hidden;
    background-color: var(--color-bg-container, ${colorToken.colorBgContainer});
  `,
  editorContent: css`
    min-height: inherit;
    position: relative;
  `,
  contentEditable: css`
    outline: none;
    padding: ${token.padding}px;
    min-height: inherit;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
    line-height: 1.6;
    color: var(--color-text-base, ${colorToken.colorTextBase});
    background-color: var(--color-bg-container, ${colorToken.colorBgContainer});
    
    &[contenteditable="true"] {
      cursor: text;
    }
    
    &[contenteditable="false"] {
      cursor: default;
    }
  `,
  placeholder: css`
    position: absolute;
    top: ${token.padding}px;
    left: ${token.padding}px;
    color: var(--color-text-placeholder, ${colorToken.colorTextPlaceholder});
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
    line-height: 1.6;
    pointer-events: none;
    user-select: none;
  `,
  toolbar: css`
    padding: ${token.paddingSM}px;
    border-bottom: 1px solid var(--color-border, ${colorToken.colorBorder});
    background-color: var(--color-bg-container, ${colorToken.colorBgContainer});
    display: flex;
    align-items: center;
    gap: ${token.marginXS}px;
  `,
  
  // @uiw/react-md-editor 自定义样式
  markdownEditor: css`
    .w-md-editor {
      background-color: transparent;
      border: none;
    }
    
    .w-md-editor-content {
      background-color: transparent;
    }
    
    .w-md-editor-input {
      background-color: var(--color-bg-container, ${colorToken.colorBgContainer});
      color: var(--color-text-base, ${colorToken.colorTextBase});
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 14px;
      line-height: 1.6;
      padding: ${token.padding}px;
    }
    
    .w-md-editor-preview {
      background-color: var(--color-bg-container, ${colorToken.colorBgContainer});
      color: var(--color-text-base, ${colorToken.colorTextBase});
      padding: ${token.padding}px;
    }
    
    // 标题样式
    h1, h2, h3, h4, h5, h6 {
      color: var(--color-text-base, ${colorToken.colorTextBase});
      font-weight: 600;
      margin: 1em 0 0.5em 0;
    }
    
    h1 {
      font-size: 2em;
      border-bottom: 2px solid var(--color-border, ${colorToken.colorBorder});
      padding-bottom: 0.3em;
    }
    
    h2 {
      font-size: 1.5em;
    }
    
    h3 {
      font-size: 1.25em;
    }
    
    // 列表样式
    ul, ol {
      margin: 1em 0 !important;
      padding-left: 2em !important;
      list-style-position: outside !important;
    }
    
    ul {
      list-style-type: disc !important;
    }
    
    ol {
      list-style-type: decimal !important;
    }
    
    li {
      margin: 0.3em 0 !important;
      display: list-item !important;
      list-style-position: outside !important;
    }
    
    // 嵌套列表样式
    ul ul {
      list-style-type: circle !important;
    }
    
    ul ul ul {
      list-style-type: square !important;
    }
    
    ol ol {
      list-style-type: lower-alpha !important;
    }
    
    ol ol ol {
      list-style-type: lower-roman !important;
    }
    
    // 代码样式
    code {
      background-color: var(--color-bg-layout, ${colorToken.colorBgLayout});
      border: 1px solid var(--color-border, ${colorToken.colorBorder});
      border-radius: 3px;
      padding: 2px 4px;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 0.9em;
    }
    
    pre {
      background-color: var(--color-bg-layout, ${colorToken.colorBgLayout});
      border: 1px solid var(--color-border, ${colorToken.colorBorder});
      border-radius: 6px;
      padding: 1em;
      margin: 1em 0;
      overflow-x: auto;
    }
    
    pre code {
      background-color: transparent;
      border: none;
      padding: 0;
    }
    
    // 表格样式
    table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid var(--color-border, ${colorToken.colorBorder});
      margin: 1em 0;
    }
    
    th, td {
      border: 1px solid var(--color-border, ${colorToken.colorBorder});
      padding: 0.5em;
      text-align: left;
    }
    
    th {
      background-color: var(--color-bg-layout, ${colorToken.colorBgLayout});
      font-weight: 600;
    }
    
    tr:nth-child(even) {
      background-color: var(--color-bg-layout, ${colorToken.colorBgLayout});
    }
    
    // 链接样式
    a {
      color: var(--color-primary, ${colorToken.colorPrimary});
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    // 引用样式
    blockquote {
      border-left: 4px solid var(--color-primary, ${colorToken.colorPrimary});
      margin: 1em 0;
      padding: 0.5em 1em;
      background-color: var(--color-bg-layout, ${colorToken.colorBgLayout});
      color: var(--color-text-secondary, ${colorToken.colorTextSecondary});
    }
    
    // 文本样式
    strong {
      font-weight: bold;
    }
    
    em {
      font-style: italic;
    }
    
    del {
      text-decoration: line-through;
    }
  `,
  
  // 状态样式
  saveStatus: css`
    font-size: ${token.fontSizeSM}px;
    color: var(--color-text-secondary, ${colorToken.colorTextSecondary});
  `,
  saveStatusSaving: css`
    color: var(--color-primary, ${colorToken.colorPrimary});
  `,
  saveStatusSaved: css`
    color: var(--color-success, ${colorToken.colorSuccess});
  `,
}));
